import { EsPeriod } from "@pages/es/lp/whatsapp-business-automation";
import blueTick from "public/blue-tick.png";
import greenTick from "public/green-tick.png";
import purpleTick from "public/purple-tick.png";
import Tick from "public/pricing-page/tick.png";
import { FaCheck } from "react-icons/fa6";
import { GoDash } from "react-icons/go";
import { Period } from ".";
import { AddonCard } from "../AddOnSection";
import { PricingPlanData } from "./Plan";
import { PricingHeaderData, PricingTableData } from "./PricingTable";

type plan = "growth" | "scale" | "pro";
type PlanObj = Partial<Record<Period, string>>;
type PlanType = Partial<Record<plan, PlanObj>>;
type PricingType = Record<string, PlanType>;

const pricing: PricingType = {
    usa: {
        growth: {
            quarterly: "$149",
            yearly: "$89",
        },
        scale: {
            quarterly: "$329",
            yearly: "$197",
        },
        pro: {
            quarterly: "$629",
            yearly: "$377",
        },
    },
    ind: {
        growth: {
            quarterly: "₹5,999",
            yearly: "₹3,599",
        },
        scale: {
            quarterly: "₹12,999",
            yearly: "₹7,799",
        },
        pro: {
            quarterly: "₹24,999",
            yearly: "₹14,999",
        },
    },
};

const conversationChargesLink = "https://docs.gallabox.com/pricing-and-billing-modules/new-per-message-pricing-effective-july-1-2025";

export const getPriceValue = (isDollar: boolean, radioValue: Period): { starter?: string; growth?: string; scale?: string; pro?: string; waLink?: string } => {
    return {
        growth: isDollar ? pricing.usa.growth?.[radioValue] : pricing.ind.growth?.[radioValue],
        scale: isDollar ? pricing.usa.scale?.[radioValue] : pricing.ind.scale?.[radioValue],
        pro: isDollar ? pricing.usa.pro?.[radioValue] : pricing.ind.pro?.[radioValue],
        waLink: conversationChargesLink,
    };
};

export const getPricingHeaderData = (isDollar: boolean, radioValue: Period): PricingHeaderData => {
    const priceDetails = getPriceValue(isDollar, radioValue);

    const planData = [
        { name: "GROWTH", price: priceDetails.growth as string, pricePeriod: `per month, billed ${radioValue.toLowerCase()}`, btnText: "Start for free" },
        { name: "SCALE", price: priceDetails.scale as string, pricePeriod: `per month, billed ${radioValue.toLowerCase()}`, btnText: "Start for free" },
        { name: "PRO", price: priceDetails.scale as string, pricePeriod: `per month, billed ${radioValue.toLowerCase()}`, btnText: "Start for free" },
    ];

    return {
        siteHeader: "Plan Comparison",
        siteText: "Find your best subscription.",
        planData: isDollar ? planData.filter((plan) => plan.name !== "STARTER") : planData,
    };
};

export const getPricingPlanData = (isDollar: boolean, radioValue: Period): PricingPlanData[] => {
    const priceDetails = getPriceValue(isDollar, radioValue);
    return [
        {
            name: "Starter",
            idealFor: "Best for teams that want to broadcast offers and promotions on the go",
            price: priceDetails.starter,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            subText: { text: "Unlimited free incoming conversations with auto-reply/routing feature" },
            waConChargesText: "WhatsApp Conversation Charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "Maximum 3 users",
            featureTitle: "Key Features",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Broadcast and auto-response configuration",
                },
                {
                    icon: Tick,
                    text: "Broadcast using Excel sheets",
                    toolTip: " Import contacts stored in Excel sheets and send bulk messages",
                },
                {
                    icon: Tick,
                    text: "30+ Integrations",
                },
                {
                    icon: Tick,
                    text: "Segmentation of contacts",
                    toolTip: "Filter contacts by conditions and send targeted campaigns",
                },
                {
                    icon: Tick,
                    text: "Shared team inbox with collaborative features",
                    toolTip: "A central hub for teams to access, manage, and collaborate on user messages seamlessly.",
                },
                {
                    icon: Tick,
                    text: "Single WhatsApp number/channel",
                    toolTip: "Use one WhatsApp Business API number to interact with users",
                },
                {
                    icon: Tick,
                    text: "Quick/canned replies",
                    toolTip: "Set shortcuts for sending instant responses for frequently asked questions",
                },
                {
                    icon: Tick,
                    text: "500+ WhatsApp message templates",
                    toolTip: "Use these sample messages as is or take inspiration to create your own",
                },
                {
                    icon: Tick,
                    text: "Basic messaging API",
                    toolTip: "Message and contact APIs",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
        {
            name: "Growth",
            idealFor: "Ideal for teams that want to kickstart their marketing and sales automation",
            price: priceDetails.growth,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            popularPlan: "MOST POPULAR",
            subText: { text: "Unlimited free incoming conversations with basic automated messaging feature" },
            waConChargesText: "WhatsApp Conversation Charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "Up to 6 users",
            featureTitle: "Everything in Starter plan, plus:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Chatbot builder with basic connectors",
                    toolTip: "Create custom chatbots with drag-and-drop features. Connectors include Google Sheets, Razorpay & Shopify",
                },
                {
                    icon: Tick,
                    text: "WhatsApp catalogs",
                    toolTip: "Connect your online shop to WhatsApp and start selling on the platform",
                },
                {
                    icon: Tick,
                    text: "Messaging and chatbot analytics",
                    toolTip: "Track WhatsApp conversation metrics",
                },
                {
                    icon: Tick,
                    text: "Facilitate enabling of blue tick verification",
                    toolTip: " A blue tick next to your business profile name signifies trust and authenticity",
                },
                {
                    icon: Tick,
                    text: "Carousel message formats",
                    toolTip: "Send interactive image/video carousel messages",
                },
                {
                    icon: Tick,
                    text: "WhatsApp Flows/Forms",
                    toolTip: "Use forms to collect user information on WhatsApp",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
        {
            name: "Scale",
            idealFor: "Perfect for teams that want to scale their WhatsApp automation capabilities",
            price: priceDetails.scale,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            subText: { text: "Unlimited free incoming conversations with advanced automation features" },
            waConChargesText: "WhatsApp Conversation Charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "Up to 6 users ",
            featureTitle: "Everything in Growth plan, plus:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Chatbot builder with advanced connectors and utilities",
                    toolTip: "Create advanced, custom chatbots. Connectors include Zoho CRM, HubSpot and API call",
                },
                {
                    icon: Tick,
                    text: "Drip campaigns",
                    toolTip: "Set up messages to be sent to users at regular intervals of time",
                },
                {
                    icon: Tick,
                    text: "WhatsApp native payment",
                    toolTip: "Set up WhatsApp-enabled payments within the messaging app",
                },
                {
                    icon: Tick,
                    text: "Advanced messaging API",
                    toolTip: "Webhooks, Broadcast and Template APIs",
                },
                {
                    icon: Tick,
                    text: "Custom roles",
                    toolTip: "Assign roles with customized permissions based on business needs",
                },
                {
                    icon: Tick,
                    text: "Multiple WhatsApp numbers/channels",
                    toolTip: "Add more than one WhatsApp Business API number for user interactions",
                },
                {
                    icon: Tick,
                    text: "AI rewrite",
                    toolTip: "Use Gallabox GPT to write clear, concise and engaging messages",
                },
                {
                    icon: Tick,
                    text: "Team management",
                    toolTip: "Create teams of specific members for efficient management of user conversations",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
        {
            name: "Custom",
            idealFor: "Tailored for large teams that need customized and comprehensive WhatsApp automation capabilities",
            subText: { text: "Unlimited free incoming conversations with customized automation features" },
            waConChargesText: "WhatsApp Conversation Charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Contact sales",
            btnLink: "https://tally.so/r/nP1Wq5",
            userLimit: "Minimum 25 users",
            featureTitle: "Everything in Scale plan, plus:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "AI-powered chatbots",
                    toolTip: "Build & train your own human like support or sales agents",
                },
                {
                    icon: Tick,
                    text: "IP restrictions",
                    toolTip: "Restrict the networks in which your team members can access Gallabox",
                },
                {
                    icon: Tick,
                    text: "Number masking",
                    toolTip: "Mask personal identifiers such as name, email and phone number of your customers within the product",
                },
                {
                    icon: Tick,
                    text: "Custom integrations",
                },
                {
                    icon: Tick,
                    text: "Other personalized requests",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
    ];
};

export const pricingTableData: (isDollar: boolean) => PricingTableData[] = (isDollar: boolean) => {
    return [
        {
            tableTitle: "BASIC",
            tableDetails: [
                {
                    title: "Number of users",
                    starter: "Maximum 3 users",
                    growth: "Up to 6 users",
                    scale: "Up to 6 users",
                    pro: "Up to 6 users",
                },
                {
                    title: "Shared team inbox",
                    tooltip: "A common inbox to manage all user conversations",
                    starter: FaCheck,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Canned responses",
                    tooltip: "Pre-written responses for repetitive questions such as welcome messages, payment reminders & more",
                    starter: FaCheck,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Multiple WhatsApp numbers",
                    tooltip: "Use separate number for each team under one account",
                    starter: GoDash,
                    growth: GoDash,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Mobile app - Android & iOS",
                    tooltip: "Use Gallabox app on your mobile to manage conversations",
                    starter: FaCheck,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Roles and permissions",
                    starter: GoDash,
                    growth: "Default",
                    scale: "Default, Custom roles",
                    pro: "Default, Custom roles",
                },
                {
                    title: "Custom roles",
                    tooltip: "Assign roles with customized permissions based on business needs",
                    starter: GoDash,
                    growth: GoDash,
                    scale: "3",
                    pro: "10",
                },
                {
                    title: "Team management",
                    tooltip: " Group specific members into teams for smoother conversation management",
                    starter: GoDash,
                    growth: GoDash,
                    scale: "3",
                    pro: "10",
                },
                {
                    title: "Integrations",
                    starter: GoDash,
                    growth: "30+ native integrations",
                    scale: "30+ native integrations",
                    pro: "30+ native integrations",
                },
                {
                    title: "Channels",
                    starter: "1",
                    growth: "1",
                    scale: "1",
                    pro: "5",
                },
                {
                    title: "Free message credits",
                    starter: isDollar ? "$2" : "INR 200",
                    growth: isDollar ? "$2" : "INR 200",
                    scale: isDollar ? "$2" : "INR 200",
                    pro: isDollar ? "$2" : "INR 200",
                },
                {
                    title: "Support",
                    starter: "Product Documentation, Support Tickets, Email Support - 72 hours SLA",
                    growth: "Product Documentation, Support Tickets, Email Support - 48 Hours SLA",
                    scale: "Product Documentation, Support Tickets, Email Support - 24 Hours SLA",
                    pro: "Product Documentation, Support Tickets, Email Support - 24 Hours SLA, Virtual Meetings, WhatsApp",
                },
            ],
        },
        {
            tableTitle: "MARKETING",
            tableDetails: [
                {
                    title: "Bulk broadcast",
                    tooltip: "Schedule and send WhatsApp messages to a selected segment of contacts.",
                    starter: FaCheck,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Maximum Broadcast limit per day",
                    tooltip: "Number of unique contacts you can send broadcast messages in a 24-hour period.",
                    starter: "10k",
                    growth: "10K",
                    scale: "100K",
                    pro: "1,000K",
                },
                {
                    title: "Drip marketing/Sequencing",
                    tooltip: " Send automated, scheduled messages at regular intervals to nurture customers & boost conversions.",
                    starter: GoDash,
                    growth: GoDash,
                    scale: "10",
                    pro: "20",
                },
                {
                    title: "Click-to-WhatsApp ads",
                    tooltip: "Drive customers from Meta ads to WhatsApp chats with a single click.",
                    starter: FaCheck,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Reply configuration",
                    tooltip: "Assign messages to a bot, human or team — depending on the business context.",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Message tracker",
                    tooltip: "Check message stats including user views, URL clicks and unsubscribes, if any",
                    starter: GoDash,
                    growth: "10",
                    scale: "20",
                    pro: "100",
                },
            ],
        },
        {
            tableTitle: "SALES",
            tableDetails: [
                {
                    title: "Bots",
                    tooltip: "Build WhatsApp chatbots using AI prompts or simple drag-and-drop tools.",
                    starter: GoDash,
                    growth: "3",
                    scale: "5",
                    pro: "10",
                },
                {
                    title: "Bot flows",
                    tooltip: "Bot flows refer to the sequence of steps and actions configured within a bot to guide customer interactions.",
                    starter: GoDash,
                    growth: "50",
                    scale: "100",
                    pro: "250",
                },
                {
                    title: "Multilingual chatbots",
                    tooltip: "Chatbots that can be customized to converse with users in their native languages",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Bot connectors",
                    tooltip: " Connect WhatsApp chatbots with your existing tools",
                    starter: GoDash,
                    growth: "Basic",
                    scale: "Advanced",
                    pro: "Advanced",
                    growthTooltip: "Google sheets, Shopify, Razorpay, Zoho Bigin, Woocommerce, Sangam CRM, PhonePe",
                    scaleTooltip: "API calls, Zoho CRM, HubSpot, Zoom, Leadsquared, OpenAI, Stripe, Elevenlabs",
                    proTooltip: "API calls, Zoho CRM, HubSpot, Zoom, Leadsquared, OpenAI, Stripe, Elevenlabs",
                },
                {
                    title: "Bot actions",
                    tooltip: " Automate tasks like sending messages, updating variables, and triggering webhooks within your chatbot flow",
                    starter: GoDash,
                    growth: "Basic",
                    scale: "Advanced",
                    pro: "Advanced",
                    growthTooltip: " Assign conversations, Unassign conversations, Resolve conversations, Update conversation fields, Update contact fields, Update company fields",
                    scaleTooltip: "Push to sequence, Add note and mention",
                    proTooltip: "Push to sequence, Add note and mention",
                },
                {
                    title: "Bot utilities",
                    tooltip: "Automate interactive conversations with tools for flow logic, user input, and routing.",
                    starter: GoDash,
                    growth: "Basic",
                    scale: "Advanced",
                    pro: "Advanced",
                    growthTooltip: "Send button option, Send list option, Condition, Set variable, Hint",
                    scaleTooltip: "Jump to, switch, Office hours, GPT Dialog, GPT Knowledge",
                    proTooltip: "Jump to, switch, Office hours, GPT Dialog, GPT Knowledge",
                },
                {
                    title: "Forms/Flows",
                    tooltip: "Collect user information using forms within WhatsApp ",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: "Dynamic forms",
                    proTooltip: "Fetch real-time data from other systems for various use cases",
                },
            ],
        },
        {
            tableTitle: "E-COMMERCE",
            tableDetails: [
                {
                    title: "WhatsApp catalogs",
                    tooltip: "Connect your online shop to WhatsApp and sell on the platform",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Native payments",
                    tooltip: "Collect payments directly in WhatsApp chats using integrated, no-code payment solutions",
                    starter: GoDash,
                    growth: "Razorpay",
                    scale: "Razorpay, WhatsApp Pay",
                    pro: "Razorpay, WhatsApp Pay",
                },
            ],
        },
        {
            tableTitle: "ANALYTICS",
            tableDetails: [
                {
                    title: "Conversational analytics",
                    tooltip: "Track metrics related to user conversations",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Campaign analytics",
                    tooltip: "Track campaign performance ",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
                {
                    title: "Bot analytics",
                    tooltip: "Get a detailed view of how your chatbot is performing and interacting with users",
                    starter: GoDash,
                    growth: FaCheck,
                    scale: FaCheck,
                    pro: FaCheck,
                },
            ],
        },
        {
            tableTitle: "API & WEBHOOKS",
            tableDetails: [
                {
                    title: "API capabilities",
                    tooltip: "Integrate your tech stack seamlessly with APIs",
                    starter: GoDash,
                    growth: "Message Contact",
                    scale: "Message Contact Broadcast Template",
                    pro: "Message  Contact Broadcast Template",
                },
                {
                    title: "API call limit",
                    starter: "100",
                    growth: "200",
                    scale: "500",
                    pro: "1,000",
                },
                {
                    title: "Webhooks",
                    tooltip: "Facilitate data exchange between your Gallabox account and any other platform your business uses",
                    starter: GoDash,
                    growth: "Contact",
                    scale: "Contact Message Conversation WhatsApp Statistics",
                    pro: "Contact Message Conversation WhatsApp Statistics",
                },
                {
                    title: "Webhook type",
                    starter: GoDash,
                    growth: GoDash,
                    scale: "Contact, Message received, Cart received, Conversation",
                    pro: "Contact, Message received, Cart received, Conversation, Template status",
                },
            ],
        },
        {
            tableTitle: "SECURITY",
            tableDetails: [
                {
                    title: "Number masking",
                    starter: GoDash,
                    growth: GoDash,
                    scale: GoDash,
                    pro: FaCheck,
                },
                {
                    title: "IP restrictions",
                    starter: GoDash,
                    growth: GoDash,
                    scale: GoDash,
                    pro: FaCheck,
                },
                {
                    title: "SSO/SAML",
                    starter: GoDash,
                    growth: GoDash,
                    scale: GoDash,
                    pro: FaCheck,
                },
            ],
        },
    ];
};

export const defaultAddonData = (isDollar: boolean): AddonCard[] => {
    return [
        {
            addOnName: "Zoho",
            description: "Integrate with Zoho to send personalized WhatsApp messages, boost engagement, and close deals. Applicable only for the Growth, Scale and Pro plans",
            price: "free",
            pricingPeriod: "",
            url: "https://assets.gallabox.com/gb-home/pricing/zoho.png",
        },
        {
            addOnName: "Shopify",
            description: "Automate WhatsApp notifications on Shopify to drive sales, recover carts, and support customers.",
            price: "$5",
            pricingPeriod: "month",
            url: "https://assets.gallabox.com/gb-home/pricing/shopify.png",
        },
        {
            addOnName: "Additional users",
            description: "Handle messages at scale by adding team members to your Gallabox account. Applicable on the Growth, Scale and Pro plans.",
            price: isDollar ? "$15" : "₹ 1,000",
            pricingPeriod: "month",
            url: "https://assets.gallabox.com/gb-home/pricing/additional-channels.png",
        },
        {
            addOnName: "Additional channels",
            description: "Connect multiple WhatsApp Business API numbers to manage conversations with different user cohorts. Available on the Scale and Pro plans.",
            price: isDollar ? "$20" : "₹ 1,200",
            pricingPeriod: "month",
            url: "https://assets.gallabox.com/gb-home/pricing/additional-users.png",
        },
        {
            addOnName: "Dedicated account manager",
            description: "Enjoy premium support for all your daily operational needs.",
            price: "₹ 15,000",
            pricingPeriod: "month",
            url: "https://assets.gallabox.com/gb-home/pricing/account-manager.png",
        },
    ];
};

type EsPlan = "stater" | "growth" | "scale";
type EsPlanObj = Partial<Record<EsPeriod, string>>;
type EsPlanType = Record<EsPlan, EsPlanObj>;
type ESPricingType = Record<string, EsPlanType>;

const EsPricing: ESPricingType = {
    usa: {
        stater: {
            mensual: "$35",
            trimestral: "$29",
            anual: "$23",
        },
        growth: {
            mensual: "$120",
            trimestral: "$99",
            anual: "$79",
        },
        scale: {
            mensual: "$300",
            trimestral: "$250",
            anual: "$200",
        },
    },
};

const usEsLink = "https://developers.facebook.com/docs/whatsapp/pricing#rate-cards";

export const getEsPriceValue = (radioValue: EsPeriod): { starter?: string; growth?: string; scale?: string; waLink?: string } => {
    return {
        starter: EsPricing.usa.stater[radioValue],
        growth: EsPricing.usa.growth[radioValue],
        scale: EsPricing.usa.scale[radioValue],
        waLink: usEsLink,
    };
};

export const getSpanishPricingPlanData = (radioValue: EsPeriod): PricingPlanData[] => {
    const priceDetails = getEsPriceValue(radioValue);
    return [
        {
            name: "Growth",
            idealFor: "Ideal para equipos que desean impulsar la automatización de marketing y ventas",
            price: priceDetails.growth,
            pricePeriod: `Por mes, facturado ${radioValue.toLowerCase()}`,

            subText: { text: "Conversaciones entrantes gratuitas ilimitadas con función básica de mensajería automatizada" },
            waConChargesText: "Cargos por conversación de WhatsApp",
            waConChargesLink: priceDetails.waLink,
            btnText: "Empieza gratis",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "Hasta 6 usuarios",
            featureTitle: "Todo en el plan Starter más:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Constructor de chatbot con conectores básicos",
                    toolTip: " Cree chatbots personalizados con funciones de arrastrar y soltar. Conectores incluidos Google Sheets, Razorpay y Shopify",
                },
                {
                    icon: Tick,
                    text: "Catalogo WhatsApp ",
                    toolTip: "Conecta tu tienda online a WhatsApp y empieza a vender en la plataforma",
                },
                {
                    icon: Tick,
                    text: "Análisis de mensajería y chatbots",
                    toolTip: "Seguimiento de métricas de conversión de WhatsApp",
                },
                {
                    icon: Tick,
                    text: "Soporte de la habilitación de la verificación con marca azul",
                    toolTip: "Una marca de verificación azul junto al nombre de su perfil comercial significa confianza y autenticidad",
                },
                {
                    icon: Tick,
                    text: "Formatos de mensajes de Carrusel ",
                    toolTip: "Envíe mensajes interactivos en carrusel de imágenes y vídeos",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
        {
            name: "Scale",
            idealFor: "Perfecto para equipos que desean ampliar sus capacidades de automatización de WhatsApp.",
            price: priceDetails.scale,
            pricePeriod: `Por mes, facturado ${radioValue.toLowerCase()}`,
            popularPlan: "MAS POPULAR",
            subText: { text: "Conversaciones entrantes ilimitadas y gratuitas con funciones de automatización avanzada" },
            waConChargesText: "Cargos por conversación de WhatsApp",
            waConChargesLink: priceDetails.waLink,
            btnText: "Empieza gratis",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "Hasta 6 usuarios",
            featureTitle: "Todo en el plan Growth más:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Generador de chatbots con conectores y utilidades avanzadas ",
                    toolTip: "Cree chatbots avanzados y personalizados. Los conectores incluyen Zoho CRM, HubSpot y API Call",
                },
                {
                    icon: Tick,
                    text: "Flujos/Formularios de WhatsApp",
                    toolTip: "Utiliza formularios para recopilar información de los usuarios en WhatsApp",
                },
                {
                    icon: Tick,
                    text: "Campañas en secuencia",
                    toolTip: "Configurar mensajes que se enviarán a los usuarios a intervalos de tiempo regulares",
                },
                {
                    icon: Tick,
                    text: "Pagos nativos de WhatsApp ",
                    toolTip: "Configurar pagos habilitados para WhatsApp dentro de la aplicación de mensajería",
                },
                {
                    icon: Tick,
                    text: "API de mensajería avanzada",
                    toolTip: "Webhooks, API de difusión y plantillas",
                },
                {
                    icon: Tick,
                    text: "Roles personalizados",
                    toolTip: "Asignar roles con permisos personalizados según las necesidades del negocio",
                },
                {
                    icon: Tick,
                    text: "Varios números/canales de WhatsApp",
                    toolTip: "Agregue más de un número de API de WhatsApp Business para las interacciones del usuario",
                },
                {
                    icon: Tick,
                    text: "Reescritura con IA",
                    toolTip: "Utilice Gallabox GPT para escribir mensajes claros, concisos y atractivos",
                },
                {
                    icon: Tick,
                    text: "Gestión de equipos",
                    toolTip: "Crea equipos de miembros específicos para una gestión eficiente de las conversaciones de los usuarios",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
        {
            name: "Custom",
            idealFor: "Diseñado para equipos grandes que necesitan capacidades de automatización de WhatsApp personalizadas e integrales",
            subText: { text: "Conversaciones entrantes ilimitadas y gratuitas con funciones de automatización personalizadas" },
            waConChargesText: "Cargos por conversación de WhatsApp",
            waConChargesLink: priceDetails.waLink,
            btnText: "Contactar ventas",
            btnLink: "https://tally.so/r/nP1Wq5",
            userLimit: "Mínimo 25 usuarios",
            featureTitle: "Todo en el plan Scale más:",
            featureDetails: [
                {
                    icon: Tick,
                    text: "Chatbots potenciados por IA",
                    toolTip: "Construya y capacite a sus propios agentes de ventas o de soporte como humanos",
                },
                {
                    icon: Tick,
                    text: "Restricción IP",
                    toolTip: "Restringe las redes en las que los miembros de tu equipo pueden acceder a Gallabox",
                },
                {
                    icon: Tick,
                    text: "Enmascaramiento de número",
                    toolTip: "Enmascare identificadores personales como nombre, correo electrónico y número de teléfono de sus clientes dentro del producto",
                },
                {
                    icon: Tick,
                    text: "Integraciones personalizadas",
                },
                {
                    icon: Tick,
                    text: "Otras peticiones personalizadas",
                },
            ],
            cardColor: "",
            textColor: "",
            benefitsColor: "",
            ctaBackgroundColor: "",
        },
    ];
};
export const getPlans = (isDollar: boolean, radioValue: Period): PricingPlanData[] => {
    const priceDetails = getPriceValue(isDollar, radioValue);
    const regionalPlans: PricingPlanData[] = [
        {
            name: "Growth",
            idealFor: "Plug-and-play chatbots, broadcasts and a shared inbox to jump-start marketing and sales automation with integrated workflows for expanding teams.",
            price: priceDetails.growth,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            subText: { text: "Unlimited free incoming conversations with basic automated messaging feature" },
            waConChargesText: "Template message charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "",
            featureDetails: [
                {
                    icon: greenTick,
                    text: "Chatbot builder with basic connectors",
                    toolTip: "Create custom chatbots with drag-and-drop features. Connectors include Google Sheets, Razorpay & Shopify",
                },
                {
                    icon: greenTick,
                    text: "Messaging and chatbot analytics",
                    toolTip: "Track WhatsApp conversation metrics",
                },
                {
                    icon: greenTick,
                    text: "Broadcast to segmented contacts",
                    toolTip: "Filter contacts by conditions and send targeted campaigns",
                },
                {
                    icon: greenTick,
                    text: "Shared team inbox with collaborative features",
                    toolTip: "A central hub for teams to seamlessly access, manage and collaborate on user messages",
                },
                {
                    icon: greenTick,
                    text: "WhatsApp Flows/Forms",
                    toolTip: "Use forms to collect user information on WhatsApp",
                },
                {
                    icon: greenTick,
                    text: "Basic messaging API",
                    toolTip: "Manage and automate your WhatsApp business communication for multiple use cases",
                },
                {
                    icon: greenTick,
                    text: "30+ Native integrations",
                    toolTip: "Connect your Gallabox account with your tech stack",
                },
                {
                    icon: greenTick,
                    text: "WhatsApp catalogs",
                    toolTip: "Connect your online shop to WhatsApp and start selling on the platform",
                },
            ],
            cardColor: "#F0FFF4",
            textColor: "#276749",
            borderColor: "#276749",
            priceTextColor: "#22543D",
            benefitsColor: "#DCFFE5",
            ctaBackgroundColor: "#1C4532",
        },
        {
            name: "Scale",
            idealFor: "Ideal for companies looking to grow with advanced WhatsApp automation capabilities + nifty team management features.",
            price: priceDetails.scale,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            popularPlan: "MOST POPULAR",
            subText: { text: "Unlimited free incoming conversations with advanced automation features" },
            waConChargesText: "Template message charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "Everything in Growth plan, plus:",
            featureDetails: [
                {
                    icon: blueTick,
                    text: "Chatbot builder with advanced connectors and utilities",
                    toolTip: "Create advanced, custom chatbots. Connectors include Zoho CRM, HubSpot and API call",
                },
                {
                    icon: blueTick,
                    text: "Drip campaigns",
                    toolTip: "Set up messages to be sent to users at regular intervals of time",
                },
                {
                    icon: blueTick,
                    text: "WhatsApp native payment",
                    toolTip: "Set up WhatsApp-enabled payments within the messaging app",
                },
                {
                    icon: blueTick,
                    text: "Advanced messaging API",
                    toolTip: "Webhooks, Broadcast and Template APIs",
                },
                {
                    icon: blueTick,
                    text: "Custom roles",
                    toolTip: "Assign roles with customized permissions based on business needs",
                },
                {
                    icon: blueTick,
                    text: "Multiple WhatsApp numbers/channels",
                    toolTip: "Add more than one WhatsApp Business API number for user interactions",
                },
                {
                    icon: blueTick,
                    text: "AI rewrite",
                    toolTip: "Use Gallabox GPT to write clear, concise and engaging messages",
                },
                {
                    icon: blueTick,
                    text: "Team management",
                    toolTip: "Create teams of specific members for efficient management of user conversations",
                },
            ],
            cardColor: "#E6F3FF",
            textColor: "#0069CC",
            priceTextColor: "#003566",
            borderColor: "#0069CC",
            benefitsColor: "#D6EBFF",
            ctaBackgroundColor: "#003566",
        },
        {
            name: "Pro",
            idealFor: "Perfect for large teams ready to unlock advanced, AI-based WhatsApp automation with enterprise-ready security.",
            subText: { text: "Unlimited free incoming conversations with customized automation features" },
            waConChargesText: "Template message charges",
            price: priceDetails.pro,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            waConChargesLink: priceDetails.waLink,
            offerText: "5% Off",
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "Everything in Scale plan, plus:",
            featureDetails: [
                {
                    icon: purpleTick,
                    text: "AI-powered chatbots",
                    toolTip: " Build & train your human-like support or sales agents",
                },
                {
                    icon: purpleTick,
                    text: "Dynamic forms",
                    toolTip: "Forms that adapt in real-time based on user inputs",
                },
                {
                    icon: purpleTick,
                    text: "Enterprise-grade security",
                    toolTip: "Secure customer data with SOC 2 standards",
                },
                {
                    icon: purpleTick,
                    text: "IP restrictions",
                    toolTip: "Restrict the networks in which your team members can access Gallabox",
                },

                {
                    icon: purpleTick,
                    text: "Number masking",
                    toolTip: "Enable your team to chat with users without revealing their phone numbers",
                },
                {
                    icon: purpleTick,
                    text: "Priority support",
                },
            ],
            cardColor: "#FAF5FF",
            textColor: "#553C9A",
            priceTextColor: "#44337A",
            borderColor: "#553C9A",
            benefitsColor: "#F3E8FF",
            ctaBackgroundColor: "#322659",
        },
    ];

    const internationalPlans: PricingPlanData[] = [
        {
            name: "Growth",
            idealFor: "Plug-and-play chatbots, broadcasts and a shared inbox to jump-start marketing and sales automation with integrated workflows for expanding teams.",
            price: priceDetails.growth,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            subText: { text: "Unlimited free incoming conversations with basic automated messaging feature" },
            waConChargesText: "Template message charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "",
            featureDetails: [
                {
                    icon: greenTick,
                    text: "Chatbot builder with basic connectors",
                    toolTip: "Create custom chatbots with drag-and-drop features. Connectors include Google Sheets, Razorpay & Shopify",
                },
                {
                    icon: greenTick,
                    text: "Messaging and chatbot analytics",
                    toolTip: "Track WhatsApp conversation metrics",
                },
                {
                    icon: greenTick,
                    text: "Broadcast to segmented contacts",
                    toolTip: "Filter contacts by conditions and send targeted campaigns",
                },
                {
                    icon: greenTick,
                    text: "Shared team inbox with collaborative features",
                    toolTip: "A central hub for teams to seamlessly access, manage and collaborate on user messages",
                },
                {
                    icon: greenTick,
                    text: "WhatsApp Flows/Forms",
                    toolTip: "Use forms to collect user information on WhatsApp",
                },
                {
                    icon: greenTick,
                    text: "Basic messaging API",
                    toolTip: "Manage and automate your WhatsApp business communication for multiple use cases",
                },
                {
                    icon: greenTick,
                    text: "30+ Native integrations",
                    toolTip: "Connect your Gallabox account with your tech stack",
                },
                {
                    icon: greenTick,
                    text: "WhatsApp catalogs",
                    toolTip: "Connect your online shop to WhatsApp and start selling on the platform",
                },
            ],
            cardColor: "#F0FFF4",
            textColor: "#276749",
            borderColor: "#276749",
            priceTextColor: "#22543D",
            benefitsColor: "#DCFFE5",
            ctaBackgroundColor: "#1C4532",
        },
        {
            name: "Scale",
            idealFor: "Ideal for companies looking to grow with advanced WhatsApp automation capabilities + nifty team management features",
            price: priceDetails.scale,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            popularPlan: "MOST POPULAR",
            subText: { text: "Unlimited free incoming conversations with advanced automation features" },
            waConChargesText: "Template message charges",
            waConChargesLink: priceDetails.waLink,
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "Everything in growth plan, plus:",
            featureDetails: [
                {
                    icon: blueTick,
                    text: "Chatbot builder with advanced connectors and utilities",
                    toolTip: "Create advanced, custom chatbots. Connectors include Zoho CRM, HubSpot and API call",
                },
                {
                    icon: blueTick,
                    text: "Drip campaigns",
                    toolTip: "Set up messages to be sent to users at regular intervals of time",
                },
                {
                    icon: blueTick,
                    text: "WhatsApp native payment",
                    toolTip: "Set up WhatsApp-enabled payments within the messaging app",
                },
                {
                    icon: blueTick,
                    text: "Advanced messaging API",
                    toolTip: "Webhooks, Broadcast and Template APIs",
                },
                {
                    icon: blueTick,
                    text: "Custom roles",
                    toolTip: "Assign roles with customized permissions based on business needs",
                },
                {
                    icon: blueTick,
                    text: "Multiple WhatsApp numbers/channels",
                    toolTip: "Add more than one WhatsApp Business API number for user interactions",
                },
                {
                    icon: blueTick,
                    text: "AI rewrite",
                    toolTip: "Use Gallabox GPT to write clear, concise and engaging messages",
                },
                {
                    icon: blueTick,
                    text: "Team management",
                    toolTip: "Create teams of specific members for efficient management of user conversations",
                },
            ],
            cardColor: "#E6F3FF",
            textColor: "#0069CC",
            priceTextColor: "#003566",
            borderColor: "#0069CC",
            benefitsColor: "#D6EBFF",
            ctaBackgroundColor: "#003566",
        },
        {
            name: "Pro",
            idealFor: "Perfect for large teams ready to unlock advanced, AI-based WhatsApp automation with enterprise-ready security",
            subText: { text: "Unlimited free incoming conversations with customized automation features" },
            waConChargesText: "Template message charges",
            price: priceDetails.pro,
            pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
            waConChargesLink: priceDetails.waLink,
            offerText: "5% Off",
            btnText: "Start for free",
            btnLink: "https://app.gallabox.com/signup",
            userLimit: "6 users",
            featureTitle: "Everything in scale plan, plus:",
            featureDetails: [
                {
                    icon: purpleTick,
                    text: "AI-powered chatbots",
                    toolTip: "Build & train your own human like support or sales agents",
                },
                {
                    icon: purpleTick,
                    text: "Dynamic forms",
                },
                {
                    icon: purpleTick,
                    text: "Enterprise-grade security",
                },
                {
                    icon: purpleTick,
                    text: "IP restrictions",
                    toolTip: "Restrict the networks in which your team members can access Gallabox",
                },

                {
                    icon: purpleTick,
                    text: "Number masking",
                    toolTip: "Mask personal identifiers such as name, email and phone number of your customers within the product",
                },
                {
                    icon: Tick,
                    text: "Custom integrations",
                },
            ],
            cardColor: "#FAF5FF",
            textColor: "#553C9A",
            priceTextColor: "#44337A",
            borderColor: "#553C9A",
            benefitsColor: "#F3E8FF",
            ctaBackgroundColor: "#322659",
        },
    ];
    if (isDollar) {
        return internationalPlans;
    }
    return regionalPlans;
};

export const getPlanComparison = (isDollar: boolean, radioValue: Period): PricingHeaderData => {
    const priceDetails = getPriceValue(isDollar, radioValue);
    const regionalPlanComparison: PricingHeaderData = {
        siteHeader: "Plan Comparison",
        siteText: "",
        planData: [
            {
                name: "GROWTH",
                price: priceDetails.growth as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#22543D",
                bgColor: "#C6F6D5",
            },
            {
                name: "SCALE",
                price: priceDetails.scale as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#003566",
                bgColor: "#CCE6FF",
            },
            {
                name: "PRO",
                price: priceDetails.pro as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#44337A",
                bgColor: "#E9D8FD",
            },
        ],
    };
    const internationalPlansComparison: PricingHeaderData = {
        siteHeader: "Plan Comparison",
        siteText: "",
        planData: [
            {
                name: "GROWTH",
                price: priceDetails.growth as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#22543D",
                bgColor: "#C6F6D5",
            },
            {
                name: "SCALE",
                price: priceDetails.scale as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#003566",
                bgColor: "#CCE6FF",
            },
            {
                name: "PRO",
                price: priceDetails.pro as string,
                pricePeriod: `per month, billed ${radioValue.toLowerCase()}`,
                btnText: "Start for free",
                color: "#44337A",
                bgColor: "#E9D8FD",
            },
        ],
    };
    if (isDollar) {
        return internationalPlansComparison;
    }
    return regionalPlanComparison;
};
